import type { ComponentType, LazyExoticComponent } from 'react'
import type { OmniRemoteMeta } from '@omni/objects'
import {
  Activity,
  Banknote,
  Bell,
  Briefcase,
  CalendarDays,
  Database,
  FileChartColumn,
  FileSignature,
  Hammer,
  Handshake,
  Home,
  KanbanSquare,
  LayoutTemplate,
  ListChecks,
  Mail,
  MapPin,
  FolderOpen,
  Gauge,
  GraduationCap,
  Megaphone,
  Network,
  Newspaper,
  BrainCircuit,
  MessageSquareText,
  ShieldCheck,
  CalendarCheck,
  HeartHandshake,
  KeyRound,
  Layers,
  Plug,
  Bot,
  Camera,
  ClipboardList,
  CalendarRange,
  CalendarClock,
  FolderInput,
  Inbox,
  Receipt,
  Video,
  Repeat,
  Share2,
  Store,
  TrendingUp,
  Wallet,
  ChartColumn,
  UserPlus,
  Users,
  Contact,
  Tags,
  Boxes,
  BadgePercent,
  UserCog,
  LayoutGrid,
  Palette,
  Lightbulb,
  type LucideIcon,
} from 'lucide-react'
import { activeInstance } from './instances'
// The surface→App map is swapped per build via the '@shell-surface-apps'
// alias (vite.config.ts): the default build bundles the full Omni suite,
// while a single-tenant customer build (e.g. mortgage-man) bundles ONLY its
// own shell-owned surfaces — the alias is what keeps the customer bundle
// free of every unmounted app's code.
import { surfaceApps } from '@shell-surface-apps'
import * as surfaceAppsModule from '@shell-surface-apps'
import { useMemo } from 'react'

// Metas are imported EAGERLY from each app's federation-meta.ts — that file
// exists precisely so the icon rail renders without pulling any surface's App
// into the shell's initial chunk (EDNA-2005).
import { meta as crmMeta } from '@omni-command/crm/src/federation-meta'
import { meta as financeMeta } from '@omni-command/finance/src/federation-meta'
import { meta as growthMeta } from '@omni-command/growth/src/federation-meta'
import { meta as mailMeta } from '@omni-command/mail/src/federation-meta'
import { meta as marketingMeta } from '@omni-command/marketing/src/federation-meta'
import { meta as portalMeta } from '@omni-command/portal/src/federation-meta'
import { meta as projectsMeta } from '@omni-command/projects/src/federation-meta'

export interface Surface {
  meta: OmniRemoteMeta
  Icon: LucideIcon
  /** Lazy App from the per-build surface-apps map. Undefined only for metas
   * a build carries for the disabled-module panel without bundling the App. */
  App: LazyExoticComponent<ComponentType> | undefined
}

// Command Home (WP5) is owned by the shell itself — no app package, so its
// meta lives here rather than in a federation-meta.ts.
const homeMeta: OmniRemoteMeta = {
  id: 'home',
  label: 'Home',
  icon: 'Home',
  basePath: '/home',
  contextCardSupport: [],
}

// The customer front door's front page is shell-owned too (EDNA-CC
// docs/customer-front-door.md, section 8): the Apps tab, first on the
// front-door site. Only builds whose instance sets frontDoor.domain carry it
// (__FRONT_DOOR__, vite.config.ts), so every other build is unchanged.
const frontDoorMeta: OmniRemoteMeta = {
  id: 'front-door',
  label: 'Apps',
  icon: 'LayoutGrid',
  basePath: '/apps',
  contextCardSupport: [],
}

// The Clients tab is shell-owned too (Sam, 9 Oct 2026): two views on one
// surface. Scorecard: per paying client, did they get proven work, is nothing
// stuck, is everything working (pm.client_scorecard_daily). People: everyone
// at every client, from the customer brain (pm.brain_people; it was its own
// People tab until Sam asked to combine them). Staff logins only (RLS). Only
// joins allSurfaces for instances that enable it.
const scorecardMeta: OmniRemoteMeta = {
  id: 'scorecard',
  label: 'Clients',
  icon: 'Users',
  basePath: '/scorecard',
  contextCardSupport: [],
}

// Pipeline is shell-owned too — the live deal book read from a CRM mirror in
// the tenant DB's `public` schema (first: The Mortgage Man's Trail mirror).
const pipelineMeta: OmniRemoteMeta = {
  id: 'pipeline',
  label: 'Pipeline',
  icon: 'TrendingUp',
  basePath: '/pipeline',
  contextCardSupport: [],
}

// Calendar is shell-owned too — the client follow-ups calendar (NZ Legal
// Clients → Follow-ups pattern, ported per EDNA-2609). Cadence rules +
// templates + scheduled drafts in custom.followup_*; drafts only, no sends.
const calendarMeta: OmniRemoteMeta = {
  id: 'calendar',
  label: 'Calendar',
  icon: 'CalendarDays',
  basePath: '/calendar',
  contextCardSupport: [],
}

// Templates is shell-owned too — the brand-template gallery (documents,
// decks, email). First consumer: The Mortgage Man. Only appended to
// allSurfaces for instances that enable it, so builds without it keep their
// route behaviour byte-identical (an unknown /templates path still lands on
// the default surface, exactly as before this surface existed).
const templatesMeta: OmniRemoteMeta = {
  id: 'templates',
  label: 'Templates',
  icon: 'LayoutTemplate',
  basePath: '/templates-gallery',
  contextCardSupport: [],
}

// Social is shell-owned too: every social post made for the customer and a
// calendar of when each goes out (EDNA-5205; The Mortgage Man and ONA). One
// native surface (socialposts/SocialSurface) themed per instance; it reads
// the posts.json + images each customer's repo builds, which the deploy drops
// at /social/, so the route stays clear of that folder. Only joins
// allSurfaces for instances that enable it.
const socialMeta: OmniRemoteMeta = {
  id: 'social',
  label: 'Social',
  icon: 'Share2',
  basePath: '/social-posts',
  contextCardSupport: [],
}

// Data is shell-owned too — the "what your Command Centre can see" map
// (EDNA-3104): sources, named data areas with live counts, and the honest
// gaps. Like templates it only joins allSurfaces for instances that enable
// it, so every other build keeps its route behaviour byte-identical.
const dataMeta: OmniRemoteMeta = {
  id: 'data',
  label: 'Your data',
  icon: 'Database',
  basePath: '/your-data',
  contextCardSupport: [],
}

// Website leads is shell-owned too: everyone who came in through the
// customer's own website forms (The Mortgage Man first, 22 Sep 2026), with
// whether each one has reached their CRM. Only joins allSurfaces for
// instances that enable it.
const leadsMeta: OmniRemoteMeta = {
  id: 'leads',
  label: 'Website leads',
  icon: 'UserPlus',
  basePath: '/website-leads',
  contextCardSupport: [],
}

// Property checks is shell-owned too: every check a signed-in client ran with
// the property checker on the customer's website (The Mortgage Man first,
// 7 Oct 2026), and what it told them. Only joins allSurfaces for instances
// that enable it.
const propertyChecksMeta: OmniRemoteMeta = {
  id: 'propertychecks',
  label: 'Property checks',
  icon: 'MapPin',
  basePath: '/property-checks',
  contextCardSupport: [],
}

// Strategies is shell-owned too: ways to win more clients, each one run by
// Claude from the customer's own data (The Mortgage Man first, 27 Oct 2026).
// Only joins allSurfaces for instances that enable it.
const strategiesMeta: OmniRemoteMeta = {
  id: 'strategies',
  label: 'Strategies',
  icon: 'Lightbulb',
  basePath: '/strategies',
  contextCardSupport: [],
}

// Client files is shell-owned too: everything Trail holds on a client in one
// place, history, tasks, deal steps and every document with its words read
// out (The Mortgage Man first, 8 Oct 2026). Only joins allSurfaces for
// instances that enable it.
const clientFilesMeta: OmniRemoteMeta = {
  id: 'clientfiles',
  label: 'Client files',
  icon: 'FolderOpen',
  basePath: '/client-files',
  contextCardSupport: [],
}

// Clients is shell-owned too — the plain client table over the tenant's own
// core.person / core.company rows (first consumer: ONA, 7 Sep 2026). Only
// joins allSurfaces for instances that enable it.
const clientsMeta: OmniRemoteMeta = {
  id: 'clients',
  label: 'Clients',
  icon: 'Users',
  basePath: '/clients',
  contextCardSupport: [],
}

// Advisors is shell-owned too — the advisers a brokerage works with (referral
// partners, book sellers, networks): the same read-only table + detail as
// Clients, over core.person rows tagged attributes.contact_type = 'adviser'.
const advisorsMeta: OmniRemoteMeta = {
  id: 'advisors',
  label: 'Advisors',
  icon: 'Handshake',
  basePath: '/advisors',
  contextCardSupport: [],
}

// Org chart is shell-owned too — the same AI team the Agents surface lists
// (src/lib/tmmTeam), drawn as a team under the business owner.
const orgChartMeta: OmniRemoteMeta = {
  id: 'orgchart',
  label: 'Org chart',
  icon: 'Network',
  basePath: '/org-chart',
  contextCardSupport: [],
}

// Using Claude is shell-owned too: the page that teaches a customer how the
// Command Center and Claude work together — what to say, how to start a chat
// in the right environment, the habits that get good work out of it (Sam,
// 9 Oct 2026, starting with ONA). Only instances that enable 'learn' get it.
const learnMeta: OmniRemoteMeta = {
  id: 'learn',
  label: 'Using Claude',
  icon: 'GraduationCap',
  basePath: '/learn',
  contextCardSupport: [],
}

// Reports is shell-owned too: scheduled reports built from the tenant's own
// data, previewed as the exact email, sent only once approved (Sam, 26 Sep
// 2026; demo on Enterprise DNA first). Needs the Reports module in the
// tenant DB (EDNA-Command-Center scripts/tenant/reports-module.mjs). Only
// joins allSurfaces for instances that enable it.
const reportsMeta: OmniRemoteMeta = {
  id: 'reports',
  label: 'Reports',
  icon: 'FileChartColumn',
  basePath: '/reports',
  contextCardSupport: [],
}

// Connections is shell-owned too (reads core.integration_account and starts
// OAuth flows via the tenant's `connections` Edge Function) — no app package.
const connectionsMeta: OmniRemoteMeta = {
  id: 'connections',
  label: 'Connections',
  icon: 'Plug',
  basePath: '/connections',
  contextCardSupport: [],
}

// The AI Industry Map is shell-owned too (Sam, 18 Oct 2026): every layer of
// the AI industry from energy to services, who makes money, and the openings
// by size. Static research in industry/map.json. EDNA only.
const industryMeta: OmniRemoteMeta = {
  id: 'industry',
  label: 'AI Industry Map',
  icon: 'Network',
  basePath: '/industry',
  contextCardSupport: [],
}

// Nateva's Team tab (Sam, 19 Oct 2026): what is going on across Field,
// Partners, Agreements, Yield and Carbon, read from Nateva's shared database.
// Shows only; changes are made in each app or by asking Claude. Nateva only.
const teamMeta: OmniRemoteMeta = {
  id: 'team',
  label: 'Team',
  icon: 'Activity',
  basePath: '/team',
  contextCardSupport: [],
}

// Nateva's Brand tab (Sam, 25 Oct 2026; doc nateva-brand-kit 4.7): the kit's logo, colours, type, voice and
// document templates with previews and downloads. Read-only. Nateva only.
const brandMeta: OmniRemoteMeta = {
  id: 'brand',
  label: 'Brand',
  icon: 'Palette',
  basePath: '/brand',
  contextCardSupport: [],
}

// Nateva's Health tab (Sam, 3 Nov 2026): is everything working and safe, for the admins who look after the
// systems. Three lights (Data, Software and security, Access). Shows only. Admins only. Nateva only.
const healthMeta: OmniRemoteMeta = {
  id: 'health',
  label: 'Health',
  icon: 'ShieldCheck',
  basePath: '/health',
  contextCardSupport: [],
}

const agentsMeta: OmniRemoteMeta = {
  id: 'agents',
  label: 'Agents',
  icon: 'Bot',
  basePath: '/agents',
  contextCardSupport: [],
}

// Ops is shell-owned too — reads the Omni-Ops chassis (product heartbeats,
// checks, incidents, decisions) through its dashboard Edge Function.
const opsMeta: OmniRemoteMeta = {
  id: 'ops',
  label: 'Ops',
  icon: 'Activity',
  basePath: '/ops',
  contextCardSupport: [],
}

// The field-service modules (apps/shell/src/fieldservice): work orders from
// property managers, the week by branch, job proof packs, and the repeat
// cycle. Generic for any multi-branch field-service business; first built
// for the Maximum Wash prospect demo (5 Oct 2026). Each joins allSurfaces
// only for instances that enable it, so no other build's routes change.
const workOrdersMeta: OmniRemoteMeta = {
  id: 'workorders',
  label: 'Work orders',
  icon: 'ClipboardList',
  basePath: '/work-orders',
  contextCardSupport: [],
}
const scheduleMeta: OmniRemoteMeta = {
  id: 'schedule',
  label: 'Schedule',
  icon: 'CalendarRange',
  basePath: '/schedule',
  contextCardSupport: [],
}
const proofMeta: OmniRemoteMeta = {
  id: 'proof',
  label: 'Job proof',
  icon: 'Camera',
  basePath: '/job-proof',
  contextCardSupport: [],
}
const repeatMeta: OmniRemoteMeta = {
  id: 'repeat',
  label: 'Repeat cycle',
  icon: 'Repeat',
  basePath: '/repeat-cycle',
  contextCardSupport: [],
}

// The professional-services modules (apps/shell/src/proservices): the client
// inbox, the work board, due dates, documents in, meetings and billing for
// any small accounting, tax, consulting or advice firm. First built for the
// Pinnacle Taxation prospect demo (6 Oct 2026). Each joins allSurfaces only
// for instances that enable it, so no other build's routes change.
const inboxMeta: OmniRemoteMeta = {
  id: 'inbox',
  label: 'Client inbox',
  icon: 'Inbox',
  basePath: '/client-inbox',
  contextCardSupport: [],
}
const workMeta: OmniRemoteMeta = {
  id: 'work',
  label: 'Work',
  icon: 'KanbanSquare',
  basePath: '/work',
  contextCardSupport: [],
}
const deadlinesMeta: OmniRemoteMeta = {
  id: 'deadlines',
  label: 'Due dates',
  icon: 'CalendarClock',
  basePath: '/due-dates',
  contextCardSupport: [],
}
const documentsMeta: OmniRemoteMeta = {
  id: 'documents',
  label: 'Documents in',
  icon: 'FolderInput',
  basePath: '/documents-in',
  contextCardSupport: [],
}
const meetingsMeta: OmniRemoteMeta = {
  id: 'meetings',
  label: 'Meetings',
  icon: 'Video',
  basePath: '/meetings',
  contextCardSupport: [],
}
const billingMeta: OmniRemoteMeta = {
  id: 'billing',
  label: 'Billing',
  icon: 'Receipt',
  basePath: '/billing',
  contextCardSupport: [],
}

// Optional pro-services tabs for consulting and advice firms (first: the ASDM
// Solution prospect demo, 6 Oct 2026): a builder for packs and assessments,
// proposals drafted from call notes, and client briefings on an outside
// change. Their insights-and-social tab reuses the Social id and path. Each
// joins allSurfaces only for instances that enable it.
const builderMeta: OmniRemoteMeta = {
  id: 'builder',
  label: 'Builder',
  icon: 'Hammer',
  basePath: '/builder',
  contextCardSupport: [],
}
const proposalsMeta: OmniRemoteMeta = {
  id: 'proposals',
  label: 'Proposals',
  icon: 'FileSignature',
  basePath: '/proposals',
  contextCardSupport: [],
}
const briefingsMeta: OmniRemoteMeta = {
  id: 'briefings',
  label: 'Client briefings',
  icon: 'Newspaper',
  basePath: '/briefings',
  contextCardSupport: [],
}
// The MBA Brands surfaces (apps/shell/src/mba): sales by brand, sales by
// customer group (retailers) and the sales budget, read from MBA's own `app`
// schema (7 Oct 2026). Each joins allSurfaces only for instances that enable
// it, so no other build's routes change.
const salesMeta: OmniRemoteMeta = {
  id: 'sales',
  label: 'Sales',
  icon: 'ChartColumn',
  basePath: '/sales',
  contextCardSupport: [],
}
const retailersMeta: OmniRemoteMeta = {
  id: 'retailers',
  label: 'Retailers',
  icon: 'Store',
  basePath: '/retailers',
  contextCardSupport: [],
}
// MBA Brands, second wave (9 Oct 2026): a page per brand, stock cover, the
// claims desk and the People tab (who sees what). Same gating as above.
const brandsMeta: OmniRemoteMeta = {
  id: 'brands',
  label: 'Brands',
  icon: 'Tags',
  basePath: '/brands',
  contextCardSupport: [],
}
const stockMeta: OmniRemoteMeta = {
  id: 'stock',
  label: 'Stock',
  icon: 'Boxes',
  basePath: '/stock',
  contextCardSupport: [],
}
const claimsMeta: OmniRemoteMeta = {
  id: 'claims',
  label: 'Claims',
  icon: 'BadgePercent',
  basePath: '/claims',
  contextCardSupport: [],
}
// MBA Brands Month end (20 Oct 2026): the month-end checks and the brand
// pack release, off the Reports tab so Reports is only finished reports.
const monthEndMeta: OmniRemoteMeta = {
  id: 'monthend',
  label: 'Month end',
  icon: 'CalendarCheck',
  basePath: '/month-end',
  contextCardSupport: [],
}
const peopleMeta: OmniRemoteMeta = {
  id: 'people',
  label: 'People',
  icon: 'UserCog',
  basePath: '/people',
  contextCardSupport: [],
}
const budgetMeta: OmniRemoteMeta = {
  id: 'budget',
  label: 'Budget',
  icon: 'Wallet',
  basePath: '/budget',
  contextCardSupport: [],
}
// MBA Brands Forecast (28 Oct 2026): the Supply Chain forecast in units by
// product and month, entered once and read by every report.
const forecastMeta: OmniRemoteMeta = {
  id: 'forecast',
  label: 'Forecast',
  icon: 'TrendingUp',
  basePath: '/forecast',
  contextCardSupport: [],
}

// The Notifications page behind the bell (instance.notifications; MBA Brands
// first, 30 Oct 2026). Kept off the menu with navHidden: the bell opens it.
const notificationsMeta: OmniRemoteMeta = {
  id: 'notifications',
  label: 'Notifications',
  icon: 'Bell',
  basePath: '/notifications',
  contextCardSupport: [],
}

const icons: Record<string, LucideIcon> = {
  Home,
  Bell,
  Lightbulb,
  Tags,
  Boxes,
  BadgePercent,
  UserCog,
  GraduationCap,
  Gauge,
  BrainCircuit,
  MessageSquareText,
  ShieldCheck,
  CalendarCheck,
  HeartHandshake,
  KeyRound,
  Layers,
  Store,
  Wallet,
  ChartColumn,
  Hammer,
  FileSignature,
  Newspaper,
  CalendarClock,
  FolderInput,
  Inbox,
  Receipt,
  Video,
  Camera,
  ClipboardList,
  CalendarRange,
  Repeat,
  CalendarDays,
  Database,
  FileChartColumn,
  Handshake,
  Network,
  Users,
  Contact,
  Mail,
  KanbanSquare,
  Briefcase,
  Banknote,
  TrendingUp,
  Megaphone,
  Plug,
  Bot,
  LayoutTemplate,
  ListChecks,
  Activity,
  UserPlus,
  Share2,
  MapPin,
  FolderOpen,
  Palette,
}

const enabledIds: readonly string[] = activeInstance.modules

const surfaceLabels: Partial<Record<string, string>> = activeInstance.copy?.surfaceLabels ?? {}
function labelFor(meta: OmniRemoteMeta): OmniRemoteMeta {
  const label = surfaceLabels[meta.id]
  return label ? { ...meta, label } : meta
}

// A customer instance that mounts the shell-owned Clients surface gets the
// shell-owned per-client to-do list at /projects (surface-apps.<slug>.ts),
// not the Omni suite board — so its rail icon is a checklist, not a kanban.
const projectsMetaForInstance: OmniRemoteMeta = enabledIds.includes('clients')
  ? { ...projectsMeta, icon: 'ListChecks' }
  : projectsMeta

// Rail order: Home first (it is the landing surface), then the app surfaces
// in the established order. Data, Templates and Org chart ride with the
// customer-owned surfaces but are only present at all for instances that
// enable them (see their metas) — so no other build's routes change.
const allSurfaces: Surface[] = [
  ...(__FRONT_DOOR__ && enabledIds.includes('front-door') ? [frontDoorMeta] : []),
  homeMeta,
  ...(enabledIds.includes('scorecard') ? [scorecardMeta] : []),
  pipelineMeta,
  calendarMeta,
  ...(enabledIds.includes('leads') ? [leadsMeta] : []),
  ...(enabledIds.includes('clientfiles') ? [clientFilesMeta] : []),
  ...(enabledIds.includes('propertychecks') ? [propertyChecksMeta] : []),
  ...(enabledIds.includes('workorders') ? [workOrdersMeta] : []),
  ...(enabledIds.includes('schedule') ? [scheduleMeta] : []),
  ...(enabledIds.includes('proof') ? [proofMeta] : []),
  ...(enabledIds.includes('repeat') ? [repeatMeta] : []),
  ...(enabledIds.includes('inbox') ? [inboxMeta] : []),
  ...(enabledIds.includes('work') ? [workMeta] : []),
  ...(enabledIds.includes('deadlines') ? [deadlinesMeta] : []),
  ...(enabledIds.includes('documents') ? [documentsMeta] : []),
  ...(enabledIds.includes('meetings') ? [meetingsMeta] : []),
  ...(enabledIds.includes('builder') ? [builderMeta] : []),
  ...(enabledIds.includes('proposals') ? [proposalsMeta] : []),
  ...(enabledIds.includes('briefings') ? [briefingsMeta] : []),
  ...(enabledIds.includes('billing') ? [billingMeta] : []),
  ...(enabledIds.includes('data') ? [dataMeta] : []),
  ...(enabledIds.includes('templates') ? [templatesMeta] : []),
  ...(enabledIds.includes('social') ? [socialMeta] : []),
  mailMeta,
  ...(enabledIds.includes('clients') ? [clientsMeta] : []),
  ...(enabledIds.includes('advisors') ? [advisorsMeta] : []),
  crmMeta,
  projectsMetaForInstance,
  ...(enabledIds.includes('reports') ? [reportsMeta] : []),
  ...(enabledIds.includes('strategies') ? [strategiesMeta] : []),
  ...(enabledIds.includes('sales') ? [salesMeta] : []),
  ...(enabledIds.includes('retailers') ? [retailersMeta] : []),
  ...(enabledIds.includes('budget') ? [budgetMeta] : []),
  ...(enabledIds.includes('forecast') ? [forecastMeta] : []),
  ...(enabledIds.includes('brands') ? [brandsMeta] : []),
  ...(enabledIds.includes('stock') ? [stockMeta] : []),
  ...(enabledIds.includes('claims') ? [claimsMeta] : []),
  ...(enabledIds.includes('monthend') ? [monthEndMeta] : []),
  ...(enabledIds.includes('people') ? [peopleMeta] : []),
  ...(enabledIds.includes('notifications') ? [notificationsMeta] : []),
  marketingMeta,
  financeMeta,
  growthMeta,
  portalMeta,
  connectionsMeta,
  ...(enabledIds.includes('learn') ? [learnMeta] : []),
  ...(enabledIds.includes('industry') ? [industryMeta] : []),
  ...(enabledIds.includes('team') ? [teamMeta] : []),
  ...(enabledIds.includes('brand') ? [brandMeta] : []),
  ...(enabledIds.includes('health') ? [healthMeta] : []),
  agentsMeta,
  ...(enabledIds.includes('orgchart') ? [orgChartMeta] : []),
  opsMeta,
].map((meta) => ({
  // An instance may rename a tab (copy.surfaceLabels); unset keeps the stock label.
  meta: labelFor(meta),
  Icon: (__FRONT_DOOR__ && meta.icon === 'LayoutGrid' ? LayoutGrid : undefined) ?? icons[meta.icon] ?? Briefcase,
  App: surfaceApps[meta.id],
}))

/** The surfaces this instance mounts (module gating, WP4). An instance may
 * pin its own left-nav order (`navOrder`); ids it does not list keep the
 * stock order after the pinned ones. Instances without navOrder are
 * untouched. */
const navOrder: readonly string[] = activeInstance.navOrder ?? []
const navRank = (id: string): number => {
  const i = navOrder.indexOf(id)
  return i === -1 ? navOrder.length : i
}
export const surfaces: Surface[] = allSurfaces
  .filter((s) => enabledIds.includes(s.meta.id))
  .map((s, stockIndex) => ({ s, stockIndex }))
  .sort((a, b) => navRank(a.s.meta.id) - navRank(b.s.meta.id) || a.stockIndex - b.stockIndex)
  .map(({ s }) => s)

/**
 * Per-person tab visibility. An instance's surface-apps module may export
 * `useSurfaceVisibility`, a hook returning (id) => true (show), false (this
 * person may not open it) or undefined (not known yet). Instances without
 * one show every enabled surface, exactly as before. The data behind a
 * hidden tab is guarded by the tenant database regardless; this only keeps
 * the menu honest.
 */
type SurfaceVisibility = (id: string) => boolean | undefined
const useInstanceVisibility: () => SurfaceVisibility =
  ((surfaceAppsModule as Record<string, unknown>).useSurfaceVisibility as (() => SurfaceVisibility) | undefined) ??
  (() => allVisible)
const allVisible: SurfaceVisibility = () => true

/** The visibility test for the signed-in person (see useInstanceVisibility). */
export function useSurfaceVisibility(): SurfaceVisibility {
  return useInstanceVisibility()
}

/** The surfaces the rail and palette offer this person (known-visible only). */
export function useVisibleSurfaces(): Surface[] {
  const can = useInstanceVisibility()
  return useMemo(() => surfaces.filter((s) => can(s.meta.id) === true), [can])
}

/**
 * The left nav for this person: the main list, the pages folded under "More"
 * (instance.navMore) and none of the pages another page carries as its own
 * tabs (instance.navUnder). Instances that set neither get every visible page
 * in `main`, exactly as before.
 */
const navMoreIds: readonly string[] = activeInstance.navMore ?? []
const navUnder: Partial<Record<string, string>> = activeInstance.navUnder ?? {}
// Pages kept off the nav until the customer needs them (instance.navHidden).
const navHiddenIds: readonly string[] = activeInstance.navHidden ?? []
export function useRailSurfaces(): { main: Surface[]; more: Surface[] } {
  const visible = useVisibleSurfaces()
  return useMemo(() => {
    const listed = visible.filter((s) => !navUnder[s.meta.id] && !navHiddenIds.includes(s.meta.id))
    return {
      main: listed.filter((s) => !navMoreIds.includes(s.meta.id)),
      more: listed.filter((s) => navMoreIds.includes(s.meta.id)),
    }
  }, [visible])
}

/** The nav entry to light up for the open page: its holder when it rides under another page. */
export function railIdFor(id: string | undefined): string | undefined {
  return id ? (navUnder[id] ?? id) : undefined
}

export const DEFAULT_SURFACE_PATH = surfaces[0]?.meta.basePath ?? '/home'

export function surfaceForPathname(pathname: string): Surface | undefined {
  return surfaces.find(
    (s) =>
      pathname === s.meta.basePath || pathname.startsWith(s.meta.basePath + '/'),
  )
}

/**
 * A surface that EXISTS but is not in this instance's modules. Its path must
 * render the branded "not part of your workspace" panel — never a blank page
 * and never a silent redirect (no-silent-null rule).
 */
export function disabledSurfaceForPathname(
  pathname: string,
): Surface | undefined {
  return allSurfaces.find(
    (s) =>
      !enabledIds.includes(s.meta.id) &&
      (pathname === s.meta.basePath ||
        pathname.startsWith(s.meta.basePath + '/')),
  )
}
